import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { toast } from "react-toastify";
import { Mail, Send, Filter, CheckCircle, AlertCircle, MessageCircle } from "lucide-react";
import { API } from "./adminConfig";

const SupportRequestsTab = () => {
  const [supportRequestsData, setSupportRequestsData] = useState({
    requests: [],
    total_requests: 0,
    unread_requests: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'resolved'

  // Filter requests by status
  const activeRequests = supportRequestsData.requests.filter(
    request => request.status === 'pending' || request.status === 'in_progress'
  );
  const resolvedRequests = supportRequestsData.requests.filter(
    request => request.status === 'resolved'
  );
  const currentRequests = activeTab === 'active' ? activeRequests : resolvedRequests;

  useEffect(() => {
    fetchSupportRequests();
  }, []);

  const fetchSupportRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/support-requests`);
      setSupportRequestsData(response.data);
    } catch (error) {
      console.error('Error fetching support requests:', error);
      toast.error('Error loading support requests');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (requestId) => {
    try {
      await axios.patch(`${API}/admin/support-requests/${requestId}/mark-read`);
      toast.success('Request marked as read');
      fetchSupportRequests(); // Refresh the list
    } catch (error) {
      console.error('Error marking as read:', error);
      toast.error('Error updating request status');
    }
  };

  const updateStatus = async (requestId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/support-requests/${requestId}/status`, {
        status: newStatus
      });
      toast.success(`Request marked as ${newStatus}`);
      fetchSupportRequests(); // Refresh the list
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error updating request status');
    }
  };

  const sendResponse = async () => {
    if (!responseText.trim()) {
      toast.error('Please enter a response message');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/admin/support-requests/${selectedRequest.id}/respond`, {
        message: responseText,
        admin_email: 'hello@luminellewellness.com'
      });
      
      toast.success('Response sent successfully!');
      setShowResponseModal(false);
      setSelectedRequest(null);
      setResponseText('');
      fetchSupportRequests(); // Refresh the list
    } catch (error) {
      console.error('Error sending response:', error);
      toast.error('Error sending response - logged for admin review');
    } finally {
      setSending(false);
    }
  };

  const exportToCSV = () => {
    // Get all requests (or current filtered view)
    const requestsToExport = activeTab === 'active' ? activeRequests : resolvedRequests;
    
    if (requestsToExport.length === 0) {
      toast.error('No requests to export');
      return;
    }

    // Prepare CSV headers
    const headers = ['Date', 'Name', 'Email', 'Subject', 'Message', 'Status', 'Priority', 'Read'];
    
    // Prepare CSV rows
    const rows = requestsToExport.map(request => [
      new Date(request.created_at).toLocaleString(),
      request.name || 'N/A',
      request.email || 'N/A',
      request.subject || 'N/A',
      `"${(request.message || '').replace(/"/g, '""')}"`, // Escape quotes in message
      request.status || 'pending',
      request.priority || 'normal',
      request.read ? 'Yes' : 'No'
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `feedback-export-${activeTab}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${requestsToExport.length} requests to CSV`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy"></div>
        <span className="ml-2 text-burgundy">Loading support requests...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-burgundy font-serif">Support Requests Management</h2>
        <div className="flex gap-2 items-center">
          <Button
            onClick={exportToCSV}
            variant="outline"
            className="border-burgundy text-burgundy hover:bg-burgundy hover:text-white flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export to CSV
          </Button>
          <Badge className="bg-burgundy/10 text-burgundy">
            {activeRequests.length} active
          </Badge>
          <Badge className="bg-gray-100 text-gray-700">
            {resolvedRequests.length} resolved
          </Badge>
          {supportRequestsData.unread_requests > 0 && (
            <Badge className="bg-red-500 text-white animate-pulse">
              {supportRequestsData.unread_requests} unread
            </Badge>
          )}
        </div>
      </div>

      {/* Active/Resolved Tabs */}
      <div className="flex space-x-1 bg-burgundy/5 p-1 rounded-lg max-w-md">
        <Button
          variant={activeTab === 'active' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('active')}
          className={`flex-1 ${
            activeTab === 'active' 
              ? 'bg-burgundy text-white hover:bg-burgundy/80' 
              : 'text-burgundy hover:bg-burgundy/10'
          }`}
        >
          Active Requests
          {activeRequests.length > 0 && (
            <Badge className="ml-2 bg-red-500 text-white text-xs">
              {activeRequests.length}
            </Badge>
          )}
        </Button>
        <Button
          variant={activeTab === 'resolved' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('resolved')}
          className={`flex-1 ${
            activeTab === 'resolved' 
              ? 'bg-burgundy text-white hover:bg-burgundy/80' 
              : 'text-burgundy hover:bg-burgundy/10'
          }`}
        >
          Resolved
          {resolvedRequests.length > 0 && (
            <Badge className="ml-2 bg-gray-500 text-white text-xs">
              {resolvedRequests.length}
            </Badge>
          )}
        </Button>
      </div>

      {currentRequests.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {currentRequests.map((request) => (
            <Card key={request.id} className={`hover:shadow-lg transition-all duration-300 bg-white border-burgundy/20 ${
              !request.read ? 'ring-2 ring-yellow-200 bg-yellow-50/30' : ''
            }`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-lg font-serif text-burgundy">
                        {request.subject}
                      </CardTitle>
                      {!request.read && (
                        <Badge className="bg-red-100 text-red-700 text-xs animate-pulse">
                          NEW
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 mt-2">
                      <Badge variant="outline" className="border-gold text-gold">
                        {request.priority}
                      </Badge>
                      <span className="text-sm text-burgundy/60">
                        From: <strong>{request.name}</strong> ({request.email})
                      </span>
                      <span className="text-sm text-burgundy/60">
                        {new Date(request.created_at).toLocaleDateString()} at {new Date(request.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Badge className={`${
                      request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      request.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                      request.status === 'resolved' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {request.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="text-burgundy/80 whitespace-pre-wrap">{request.message}</p>
                </div>

                {/* Admin Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      setSelectedRequest(request);
                      setShowResponseModal(true);
                    }}
                    className="bg-burgundy hover:bg-burgundy/80 text-white"
                  >
                    <MessageCircle className="h-4 w-4 mr-1" />
                    Respond
                  </Button>

                  {!request.read && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => markAsRead(request.id)}
                      className="border-green-300 text-green-700 hover:bg-green-50"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Mark as Read
                    </Button>
                  )}

                  {request.status === 'pending' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatus(request.id, 'in_progress')}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      Start Working
                    </Button>
                  )}

                  {request.status !== 'resolved' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatus(request.id, 'resolved')}
                      className="border-green-300 text-green-700 hover:bg-green-50"
                    >
                      ✓ Mark Resolved
                    </Button>
                  )}

                  {request.status === 'resolved' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatus(request.id, 'pending')}
                      className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    >
                      ↺ Reopen
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <MessageCircle className="h-16 w-16 text-burgundy/30 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-burgundy/60 mb-2 font-serif">
            {activeTab === 'active' ? 'No Active Support Requests' : 'No Resolved Requests'}
          </h3>
          <p className="text-burgundy/50 mb-4">
            {activeTab === 'active' 
              ? 'When users submit help requests through the help button, they will appear here.'
              : 'Resolved support requests will be archived here.'
            }
          </p>
        </div>
      )}

      {/* Response Modal */}
      {showResponseModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-auto bg-white">
            <CardHeader>
              <CardTitle className="flex justify-between items-center text-burgundy font-serif">
                <span>Respond to: {selectedRequest.subject}</span>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowResponseModal(false);
                    setSelectedRequest(null);
                    setResponseText('');
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
              <CardDescription>
                Responding to {selectedRequest.name} ({selectedRequest.email})
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Original Message */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-burgundy mb-2">Original Message:</h4>
                <p className="text-sm text-burgundy/70">{selectedRequest.message}</p>
              </div>

              {/* Response Textarea */}
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  Your Response:
                </label>
                <Textarea
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Type your response here... This will be sent to the user's email."
                  className="border-burgundy/30 focus:border-gold min-h-[120px]"
                  rows={6}
                />
              </div>

              {/* Quick Response Templates */}
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  Quick Templates:
                </label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setResponseText("Thank you for contacting Luminelle Support. We've received your request and will get back to you within 24 hours.\n\nBest regards,\nLuminelle Support Team")}
                    className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                  >
                    Acknowledgment
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setResponseText("Thank you for reaching out. We've resolved your issue. Please let us know if you need any further assistance.\n\nBest regards,\nLuminelle Support Team")}
                    className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                  >
                    Resolution
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setResponseText("Thank you for your inquiry. To better assist you, could you please provide more details about your specific situation?\n\nBest regards,\nLuminelle Support Team")}
                    className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                  >
                    More Info
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => {
                    setShowResponseModal(false);
                    setSelectedRequest(null);
                    setResponseText('');
                  }}
                  variant="outline"
                  className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendResponse}
                  disabled={sending}
                  className="bg-burgundy hover:bg-burgundy/80 text-white flex-1"
                >
                  {sending ? (
                    <>
                      <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                      Sending Response...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Send Response & Mark as Resolved
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// FAQ Management Tab

export default SupportRequestsTab;
